// FormInput.tsx
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import Dropdown, { Option } from "./dropdown";
import { SearchIcon } from "./icons";

// input type 종류
type InputType = "text" | "number" | "dropdown" | "address" | "date";

type Props = {
  label: string;
  required?: boolean;
  type: InputType;
  name: string;
  value: string;
  placeholder?: string;
  onChange: (name: string, value: string) => void;
  dropdownOptions?: Option[];
  onOpenAddressModal?: () => void;
  onOpenDateModal?: () => void;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const IconWrapper = styled.div`
  color: #64748b;
  font-size: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 500;
`;

const RequiredMark = styled.span`
  color: #ff3a17;
`;

const InputBox = styled.input`
  padding: 10px 12px;
  outline: 1px solid #cbd5e1;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  &:focus {
    outline: 2px solid #0c8ce9;
  }
`;

const ClickableBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  padding: 10px 12px;
  outline: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  background-color: white;
  color: black;
`;

export default function FormInput({
  label,
  required = false,
  type,
  name,
  value,
  placeholder,
  onChange,
  dropdownOptions = [],
  onOpenAddressModal,
  onOpenDateModal,
}: Props) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Wrapper>
      <Label>
        {label} {required && <RequiredMark>*</RequiredMark>}
      </Label>

      {type === "text" || type === "number" ? (
        <InputBox
          type={type}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
        />
      ) : type === "dropdown" ? (
        <Dropdown
          value={value}
          options={dropdownOptions}
          placeholder={placeholder}
          onChange={(val) => onChange(name, val)}
        />
      ) : type === "address" ? (
        <ClickableBox onClick={onOpenAddressModal}>
          <IconWrapper>
            <SearchIcon />
          </IconWrapper>
          {value || "주소검색"}
        </ClickableBox>
      ) : type === "date" ? (
        <ClickableBox onClick={onOpenDateModal}>
          📅 {value || "날짜 선택"}
        </ClickableBox>
      ) : null}
    </Wrapper>
  );
}
